import { useMemo } from "react";
import { ConnectProto } from "@racoon/api";
import { fromTimestamp } from "@racoon/lib";
import { DateValue } from "~/shared/lib/analytics-utils";
import { byDateThenPickupLocation, containerVolume } from "./job-report";
import { ReportJob, useReportJobs } from "./use-report-data";
import { NON_COLLECTION_STATUSES, useReportFilterValues } from "./use-report-filters";

/** One row of «Детальный отчёт по невывозу» (AIS 15.1.7): a pickup that did not happen. */
export interface NonCollectionRow {
  jobId: bigint;
  twStartAt: Date | null;
  pickupLocationId?: bigint;
  pickupLocationName: string;
  hasPhotoReport: boolean;
  serviceReceiverId?: bigint;
  serviceReceiverName: string;
  serviceReceiverBin: string;
  containerTypeName: string;
  driverId?: bigint;
  driverName: string;
  truckId?: bigint;
  truckPlateNumber: string;
  numberOfContainers: number | null;
  containerVolume: number | null;
  driverComment: string;
  /** Short JobStatus name, "NOT_COMPLETED" or "CANCELLED". */
  status: string;
}

const toRow = (job: ReportJob): NonCollectionRow => ({
  jobId: job.id,
  twStartAt: fromTimestamp(job.twStartAt),
  pickupLocationId: job.pickupLocationId,
  pickupLocationName: job.pickupLocationName ?? "",
  hasPhotoReport: job.hasPhotoReport,
  serviceReceiverId: job.serviceReceiverId,
  serviceReceiverName: job.serviceReceiverName ?? "",
  serviceReceiverBin: job.serviceReceiverBin ?? "",
  containerTypeName: job.containerTypeName ?? "",
  driverId: job.driverId,
  driverName: job.driverName ?? "",
  truckId: job.truckId,
  truckPlateNumber: job.truckPlateNumber ?? "",
  numberOfContainers: job.numberOfContainers ?? null,
  containerVolume: containerVolume(job),
  driverComment: job.driverComment ?? "",
  status: ConnectProto.JobStatus[job.status] ?? "UNDEFINED",
});

/** Not-completed and cancelled jobs due in the period; the job status filter picks one of the two. */
export function useNonCollectionRows(startDate: DateValue, endDate: DateValue) {
  const { jobStatus, driver, truck } = useReportFilterValues();
  const {
    rows: jobs,
    isLoading,
    error,
  } = useReportJobs(startDate, endDate, NON_COLLECTION_STATUSES, { driver, truck });

  const rows = useMemo(
    () =>
      jobs
        .map(toRow)
        .filter((row) => !jobStatus || row.status === jobStatus)
        .sort(byDateThenPickupLocation((row) => row.twStartAt)),
    [jobs, jobStatus],
  );

  return { rows, isLoading, error };
}
